import React, { useState, useEffect } from 'react';
import { DocumentTextIcon, CloudIcon, EnvelopeIcon, EyeIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { collection, query, where, orderBy, limit, startAfter, getDocs, getCountFromServer, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../components/UserContext';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { Button } from '../components/button';
import { Dialog, DialogTitle, DialogBody, DialogActions } from '../components/dialog';
import { labelForEmailRow } from '../utils/emailLog';
import StorageFilesModal from '../components/StorageFilesModal';

// Helper to format bytes to human readable
const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper to convert GB to bytes
const gbToBytes = (gb) => gb * 1024 * 1024 * 1024;

// Progress bar component
const UsageProgressBar = ({ used, limit, label, icon: Icon, formatUsed, formatLimit, warningThreshold = 70, criticalThreshold = 90, allowOveruse = false }) => {
  const percent = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;

  const getProgressColor = () => {
    if (percent >= criticalThreshold) return 'bg-red-500';
    if (percent >= warningThreshold) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getBackgroundColor = () => {
    if (percent >= criticalThreshold) return 'bg-red-100';
    if (percent >= warningThreshold) return 'bg-yellow-100';
    return 'bg-gray-200';
  };

  return (
    <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-azul-surelia/10 rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5 text-azul-surelia" />
        </div>
        <div className="flex items-center gap-2">
          <Heading level={3} className="text-lg font-semibold text-azul-surelia">
            {label}
          </Heading>
          {allowOveruse && (
            <div className="relative group">
              <InformationCircleIcon className="w-4 h-4 text-azul-surelia/50 cursor-help" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                Si excedes el limite, se cobrara el diferencial
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Text className="text-sm text-azul-surelia/70">Usado</Text>
          <Text className="text-sm font-medium text-azul-surelia">
            {formatUsed} / {formatLimit}
          </Text>
        </div>

        <div className={`w-full ${getBackgroundColor()} rounded-full h-3`}>
          <div
            className={`h-3 rounded-full transition-all duration-500 ${getProgressColor()}`}
            style={{ width: `${percent}%` }}
          />
        </div>

        <div className="flex items-center justify-between">
          <Text className="text-xs text-azul-surelia/50">
            {percent.toFixed(1)}% utilizado
          </Text>
          {percent >= criticalThreshold && (
            <Text className="text-xs text-red-600 font-medium">
              Limite casi alcanzado
            </Text>
          )}
          {percent >= warningThreshold && percent < criticalThreshold && (
            <Text className="text-xs text-yellow-600 font-medium">
              Uso elevado
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

// Months of email history shown (current month first).
const EMAIL_USAGE_MONTHS = 6;

// Correos enviados por mes: Firestore count() aggregations over email_log
// (orgId == + sentAt range — served by the email_log orgId+sentAt composite
// index, the same one the Correos enviados list in Organización uses).
// Counts every logged attempt of the org: bienvenidas, avisos de acceso,
// solicitudes/recordatorios y fianzas emitidas.
const EMAIL_DETAIL_PAGE_SIZE = 50;

const EmailUsageCard = ({ orgId }) => {
  const [months, setMonths] = useState(null); // [{key, label, count, start, end}] newest first
  const [error, setError] = useState(false);
  // Month drill-down: the emails behind one month's count.
  const [detailMonth, setDetailMonth] = useState(null); // {key, label, start, end}
  const [detailRows, setDetailRows] = useState(null); // null while loading
  const [detailError, setDetailError] = useState(false);
  const [detailCursor, setDetailCursor] = useState(null);
  const [detailHasMore, setDetailHasMore] = useState(false);
  const [detailLoadingMore, setDetailLoadingMore] = useState(false);

  const fetchDetail = async (month, cursor = null) => {
    const parts = [
      collection(db, 'email_log'),
      where('orgId', '==', orgId),
      where('sentAt', '>=', Timestamp.fromDate(month.start)),
      where('sentAt', '<', Timestamp.fromDate(month.end)),
      orderBy('sentAt', 'desc'),
    ];
    if (cursor) parts.push(startAfter(cursor));
    parts.push(limit(EMAIL_DETAIL_PAGE_SIZE));
    const snap = await getDocs(query(...parts));
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setDetailRows((prev) => (cursor && prev ? [...prev, ...rows] : rows));
    setDetailCursor(snap.docs[snap.docs.length - 1] || null);
    setDetailHasMore(snap.docs.length === EMAIL_DETAIL_PAGE_SIZE);
  };

  const openMonth = async (month) => {
    setDetailMonth(month);
    setDetailRows(null);
    setDetailError(false);
    setDetailCursor(null);
    setDetailHasMore(false);
    try {
      await fetchDetail(month);
    } catch (e) {
      console.error('Failed to load month emails:', e);
      setDetailError(true);
    }
  };

  const loadMoreDetail = async () => {
    if (!detailMonth || !detailCursor) return;
    setDetailLoadingMore(true);
    try {
      await fetchDetail(detailMonth, detailCursor);
    } catch (e) {
      console.error('Failed to load more month emails:', e);
      setDetailError(true);
    } finally {
      setDetailLoadingMore(false);
    }
  };

  const formatDetailDate = (ts) => {
    const d = ts?.toDate ? ts.toDate() : null;
    if (!d) return '—';
    return d.toLocaleString('es-MX', {
      day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit',
    });
  };

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setMonths(null);
    setError(false);
    (async () => {
      try {
        const now = new Date();
        const results = await Promise.all(
          Array.from({ length: EMAIL_USAGE_MONTHS }, (_, i) => {
            const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const end = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
            const label = start.toLocaleDateString('es-MX', { month: 'short' })
              + (start.getFullYear() !== now.getFullYear() ? ` ${String(start.getFullYear()).slice(2)}` : '');
            return getCountFromServer(query(
              collection(db, 'email_log'),
              where('orgId', '==', orgId),
              where('sentAt', '>=', Timestamp.fromDate(start)),
              where('sentAt', '<', Timestamp.fromDate(end)),
              // Matches the (orgId asc, sentAt desc) composite index exactly.
              orderBy('sentAt', 'desc'),
            )).then((snap) => ({
              key: `${start.getFullYear()}-${start.getMonth() + 1}`,
              label,
              count: snap.data().count,
              start,
              end,
            }));
          })
        );
        if (!cancelled) setMonths(results);
      } catch (e) {
        console.error('Failed to load email usage:', e);
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  const maxCount = months ? Math.max(1, ...months.map((m) => m.count)) : 1;

  return (
    <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-azul-surelia/10 rounded-lg flex items-center justify-center">
          <EnvelopeIcon className="w-5 h-5 text-azul-surelia" />
        </div>
        <Heading level={3} className="text-lg font-semibold text-azul-surelia">
          Correos Enviados
        </Heading>
      </div>

      {error ? (
        <Text className="text-sm text-azul-surelia/70">No se pudo cargar el conteo de correos.</Text>
      ) : months === null ? (
        <Text className="text-sm text-azul-surelia/70">Cargando correos enviados...</Text>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-end gap-6">
          <div className="flex-shrink-0">
            <Text className="text-xs font-medium text-azul-surelia/70 mb-1">Este mes</Text>
            <Text className="text-3xl font-bold text-azul-surelia">
              {months[0].count.toLocaleString()}
            </Text>
          </div>
          <div className="flex-1 grid grid-cols-6 gap-2">
            {[...months].reverse().map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => m.count > 0 && openMonth(m)}
                className={`flex flex-col items-center gap-1 rounded-lg pt-1 pb-0.5 transition-colors ${
                  m.count > 0 ? 'cursor-pointer hover:bg-azul-surelia/5' : 'cursor-default'
                }`}
                title={m.count > 0 ? 'Ver correos del mes' : undefined}
              >
                <div className="w-full h-16 flex items-end justify-center">
                  <div
                    className="w-4 rounded-t bg-azul-surelia/60"
                    style={{ height: `${Math.max(m.count > 0 ? 8 : 2, (m.count / maxCount) * 100)}%` }}
                  />
                </div>
                <Text className="text-[11px] text-azul-surelia/60 capitalize">{m.label}</Text>
                <Text className="text-xs font-medium text-azul-surelia">{m.count.toLocaleString()}</Text>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Month drill-down: which emails were sent that month */}
      <Dialog
        size="2xl"
        open={!!detailMonth}
        onClose={() => setDetailMonth(null)}
      >
        <DialogTitle className="capitalize">
          Correos de {detailMonth?.start?.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}
        </DialogTitle>
        <DialogBody>
          {detailError ? (
            <Text className="text-sm text-red-700">No se pudieron cargar los correos.</Text>
          ) : detailRows === null ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-azul-surelia"></div>
            </div>
          ) : detailRows.length === 0 ? (
            <Text className="text-sm text-zinc-500 py-2">Sin correos este mes.</Text>
          ) : (
            <>
              <div className="divide-y divide-azul-surelia/10 max-h-[60vh] overflow-y-auto">
                {detailRows.map((row) => (
                  <div key={row.id} className="py-2.5 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Text className="text-sm font-medium text-zinc-800">{labelForEmailRow(row)}</Text>
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${
                          row.status === 'sent'
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : 'bg-red-50 text-red-700 border-red-200'
                        }`}>
                          {row.status === 'sent' ? 'Enviado' : 'Falló'}
                        </span>
                      </div>
                      <Text className="text-xs text-zinc-600 break-words">
                        {row.toName ? `${row.toName} — ${row.to}` : row.to}
                      </Text>
                      {Array.isArray(row.items) && row.items.length > 0 && (
                        <Text className="text-xs text-zinc-400 break-words">
                          {row.items.map((it) => `${it.tipo}: ${it.nombre}`).join(' · ')}
                        </Text>
                      )}
                    </div>
                    <Text className="text-xs text-zinc-500 whitespace-nowrap flex-shrink-0">
                      {formatDetailDate(row.sentAt)}
                    </Text>
                  </div>
                ))}
              </div>
              {detailHasMore && (
                <div className="mt-3 text-center">
                  <button
                    type="button"
                    onClick={loadMoreDetail}
                    disabled={detailLoadingMore}
                    className="text-sm font-medium text-azul-surelia hover:underline disabled:opacity-50"
                  >
                    {detailLoadingMore ? 'Cargando...' : 'Cargar más'}
                  </button>
                </div>
              )}
            </>
          )}
        </DialogBody>
        <DialogActions>
          <Button color="white" onClick={() => setDetailMonth(null)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const ConfiguracionUso = ({
  plan,
  planLoading,
  usage,
  usageLoading,
  storageUsage,
  storageUsageLoading,
  orgData
}) => {
  const [showFilesModal, setShowFilesModal] = useState(false);
  const { userData } = useUser();

  // Pages usage
  const pageLimit = plan?.page_limit || 0;
  const pagesUsed = usage?.total_pages || 0;

  // Storage usage (plan.storage is in GB, storageUsage.total_bytes is in bytes)
  const storageLimitGB = plan?.storage || 0;
  const storageLimitBytes = gbToBytes(storageLimitGB);
  const storageUsedBytes = storageUsage?.total_bytes || 0;
  const fileCount = storageUsage?.file_count || 0;

  // Get current month name
  const currentMonth = new Date().toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });

  if (planLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6">
          <Text className="text-azul-surelia/70">Cargando informacion del plan...</Text>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="space-y-6">
        <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6">
          <Text className="text-azul-surelia/70">No hay informacion de plan disponible.</Text>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <Heading level={2} className="text-xl font-bold text-azul-surelia">
              Uso de Recursos
            </Heading>
            <Text className="text-azul-surelia/70 mt-1">
              Plan {plan.name} - {orgData?.nombre || 'Tu organizacion'}
            </Text>
          </div>
          <div className="text-right">
            <Text className="text-sm text-azul-surelia/50">Periodo actual</Text>
            <Text className="text-sm font-medium text-azul-surelia capitalize">{currentMonth}</Text>
          </div>
        </div>
      </div>

      {/* Usage Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pages Usage */}
        {usageLoading ? (
          <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6">
            <Text className="text-azul-surelia/70">Cargando uso de paginas...</Text>
          </div>
        ) : (
          <UsageProgressBar
            used={pagesUsed}
            limit={pageLimit}
            label="Paginas Procesadas"
            icon={DocumentTextIcon}
            formatUsed={pagesUsed.toLocaleString()}
            formatLimit={pageLimit.toLocaleString()}
            allowOveruse={!plan.limit_enforced}
          />
        )}

        {/* Storage Usage */}
        {storageUsageLoading ? (
          <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6">
            <Text className="text-azul-surelia/70">Cargando uso de almacenamiento...</Text>
          </div>
        ) : (
          <UsageProgressBar
            used={storageUsedBytes}
            limit={storageLimitBytes}
            label="Almacenamiento"
            icon={CloudIcon}
            formatUsed={formatBytes(storageUsedBytes)}
            formatLimit={`${storageLimitGB} GB`}
            allowOveruse={!plan.limit_enforced}
          />
        )}
      </div>

      {/* Emails sent per month */}
      <EmailUsageCard orgId={userData?.org} />

      {/* Additional Stats */}
      <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6">
        <Heading level={3} className="text-lg font-semibold text-azul-surelia mb-4">
          Detalles Adicionales
        </Heading>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-azul-surelia/5 rounded-lg p-4">
            <Text className="text-xs font-medium text-azul-surelia/70 mb-2">Paginas Disponibles</Text>
            <Text className="text-xl font-bold text-azul-surelia">
              {Math.max(0, pageLimit - pagesUsed).toLocaleString()}
            </Text>
          </div>
          <div className="bg-azul-surelia/5 rounded-lg p-4">
            <Text className="text-xs font-medium text-azul-surelia/70 mb-2">Almacenamiento Disponible</Text>
            <Text className="text-xl font-bold text-azul-surelia">
              {formatBytes(Math.max(0, storageLimitBytes - storageUsedBytes))}
            </Text>
          </div>
          <div className="bg-azul-surelia/5 rounded-lg p-4">
            <Text className="text-xs font-medium text-azul-surelia/70 mb-2">Archivos Almacenados</Text>
            <div className="flex items-center justify-between">
              <Text className="text-xl font-bold text-azul-surelia">
                {fileCount.toLocaleString()}
              </Text>
              <button
                onClick={() => setShowFilesModal(true)}
                className="flex items-center gap-1 text-xs text-azul-surelia hover:text-azul-surelia/80 hover:underline"
              >
                <EyeIcon className="w-3.5 h-3.5" />
                Ver detalle
              </button>
            </div>
          </div>
          <div className="bg-azul-surelia/5 rounded-lg p-4">
            <Text className="text-xs font-medium text-azul-surelia/70 mb-2">Costo por Pagina Extra</Text>
            <Text className="text-xl font-bold text-azul-surelia">
              {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(plan.extra_page_price || 0)}
            </Text>
          </div>
        </div>
      </div>

      {/* Info Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <Text className="text-sm text-blue-800">
          <strong>Nota:</strong> El uso de paginas se reinicia cada mes. El almacenamiento es acumulativo.
          Si excedes los limites de tu plan, se cobrara el diferencial segun las tarifas establecidas.
        </Text>
      </div>

      {/* Storage Files Modal */}
      <StorageFilesModal
        isOpen={showFilesModal}
        onClose={() => setShowFilesModal(false)}
        orgId={orgData?.id}
      />
    </div>
  );
};

export default ConfiguracionUso;
